// Currency input — displays formatted dollar value, stores numeric amount.
// Default mode is integer dollars (legacy callers: insurance coverage,
// contract head value). Opt-in `allowDecimal` permits up to 2 fractional
// digits and rounds to cents on commit so milestone payments can carry
// the same precision as SOW values and variation impacts.
//
// Decimal mode keeps a small piece of local state for the typed display
// so transient inputs ("10000." mid-typing, or a trailing zero "10.50")
// stay visible to the user even though the committed numeric value
// snaps to the rounded cent. The local display resyncs to the prop
// whenever the parent commits a value the local state cannot represent
// (e.g. external clear, edit-mode hydration).

import { useCallback, useEffect, useRef, useState } from "react";

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  /** When true, accept up to 2 decimal places and round to cents on
   *  commit. When false (default) input is restricted to digits and
   *  parsed as an integer. */
  allowDecimal?: boolean;
}

const FORMATTER_INTEGER = new Intl.NumberFormat("en-AU");

/** Format a numeric value for the decimal-mode display. Trailing-zero
 *  cents are preserved (10.5 → "10.50") so the surface matches how
 *  SOW/variation values render. Used for the initial render and
 *  whenever the local typed-state needs to resync to the prop. */
function formatDecimalValue(value: number): string {
  if (value === 0) return "0";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const cents = Math.round(abs * 100);
  const intPart = Math.trunc(cents / 100);
  const fractional = cents % 100;
  const intDisplay = FORMATTER_INTEGER.format(intPart);
  if (fractional === 0) return `${sign}${intDisplay}`;
  const fracDisplay = fractional.toString().padStart(2, "0");
  return `${sign}${intDisplay}.${fracDisplay}`;
}

/** Group the integer part of a typed decimal display with thousands
 *  separators while preserving the user's fractional input verbatim
 *  (incl. the transient cases "1234." and "1234.5"). */
function reformatTypedDecimal(typed: string): string {
  if (typed === "" || typed === "0") return typed;
  const dotIdx = typed.indexOf(".");
  if (dotIdx === -1) {
    const n = Number(typed);
    if (!Number.isFinite(n)) return typed;
    return FORMATTER_INTEGER.format(n);
  }
  const intPart = typed.slice(0, dotIdx);
  const decPart = typed.slice(dotIdx + 1);
  if (intPart === "") return `.${decPart}`;
  const n = Number(intPart);
  if (!Number.isFinite(n)) return typed;
  return `${FORMATTER_INTEGER.format(n)}.${decPart}`;
}

/** Strip non-digits / extra dots from raw input, capping fractional
 *  digits at 2. Returns the canonical raw form (no thousands
 *  separators). */
function rawFromTyped(input: string): string {
  const cleaned = input.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  const intPart = cleaned.slice(0, firstDot);
  const decPart = cleaned
    .slice(firstDot + 1)
    .replace(/\./g, "")
    .slice(0, 2);
  return decPart.length > 0 ? `${intPart}.${decPart}` : `${intPart}.`;
}

export default function CurrencyInput({
  value,
  onChange,
  id,
  className = "input",
  disabled,
  allowDecimal = false,
}: CurrencyInputProps) {
  // Decimal mode keeps a typed-display buffer so partial input ("12.")
  // and trailing-zero cents survive controlled re-renders. The buffer
  // is the source of truth for the input element; numeric commits flow
  // out via onChange. Whenever the parent's `value` prop diverges from
  // what the buffer parses to, we resync the buffer (handles external
  // clears and edit-mode hydration without losing in-flight typing).
  const [typedDisplay, setTypedDisplay] = useState<string>(() =>
    allowDecimal ? formatDecimalValue(value) : "",
  );
  const lastCommittedRef = useRef<number>(value);

  useEffect(() => {
    if (!allowDecimal) return;
    if (value !== lastCommittedRef.current) {
      // Parent committed a value we did not just emit — resync the
      // displayed buffer.
      setTypedDisplay(formatDecimalValue(value));
      lastCommittedRef.current = value;
    }
  }, [value, allowDecimal]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (allowDecimal) {
        const raw = rawFromTyped(e.target.value);
        const display = reformatTypedDecimal(raw);
        setTypedDisplay(display);
        const numericRaw = raw.endsWith(".") ? raw.slice(0, -1) : raw;
        if (numericRaw === "") {
          lastCommittedRef.current = 0;
          onChange(0);
          return;
        }
        const n = Number(numericRaw);
        if (!Number.isFinite(n)) {
          lastCommittedRef.current = 0;
          onChange(0);
          return;
        }
        // Round to 2dp so the wire payload satisfies the backend
        // monetaryAmount invariant (e.g. 19.999 collapses to 20).
        const rounded = Math.round(n * 100) / 100;
        lastCommittedRef.current = rounded;
        onChange(rounded);
        return;
      }
      const raw = e.target.value.replace(/[^0-9]/g, "");
      const parsed = parseInt(raw, 10);
      onChange(isNaN(parsed) ? 0 : parsed);
    },
    [onChange, allowDecimal],
  );

  const formatted = allowDecimal
    ? typedDisplay
    : value === 0
    ? "0"
    : value.toLocaleString("en-AU");

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
        $
      </span>
      <input
        id={id}
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        value={formatted}
        onChange={handleChange}
        className={`${className} pl-7`}
        disabled={disabled}
        aria-label={id ? undefined : "Value"}
      />
    </div>
  );
}
